import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Shield, Key, UserCheck, Crown } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/providers/AuthProvider';

const SUPER_ADMIN_CONFIG = {
  email: 'milan.krnjajic@gmail.com',
  name: 'Milan Krnjajić',
  permissions: [
    'super_admin',
    'admin_dashboard',
    'user_management',
    'content_moderation',
    'system_settings',
    'financial_oversight',
    'analytics_access',
    'security_management',
    'platform_configuration',
    'emergency_controls',
    'audit_logs',
    'developer_tools'
  ]
};

export function SuperAdminInit() {
  const { user, userData, updateUserData } = useAuth();
  const { toast } = useToast();
  const [isInitializing, setIsInitializing] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);

  const isSuperAdmin = userData?.role === 'superadmin' && userData?.email === SUPER_ADMIN_CONFIG.email;
  const isTargetUser = user?.email === SUPER_ADMIN_CONFIG.email;

  const initializeSuperAdmin = async () => {
    if (!user || !isTargetUser) {
      toast({
        title: 'Access Denied',
        description: 'Only Milan Krnjajić can initialize super admin access.',
        variant: 'destructive',
      });
      return;
    }

    setIsInitializing(true);
    
    try {
      // Update user data with super admin privileges
      const superAdminData = {
        role: 'superadmin' as const,
        isAdmin: true,
        isSeller: true, // Grant all privileges
        permissions: SUPER_ADMIN_CONFIG.permissions,
        superAdminInitialized: true,
        superAdminInitializedAt: new Date().toISOString(),
      };

      await updateUserData(superAdminData);
      
      // Also update directly in Firestore to ensure consistency
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...superAdminData,
        updatedAt: new Date().toISOString(),
      });

      toast({
        title: 'Super Admin Initialized',
        description: 'Welcome, Milan! You now have full super admin access to InTouch.',
      });

      // Reload the page to refresh navigation
      window.location.reload();
    } catch (error) {
      console.error('Error initializing super admin:', error);
      toast({
        title: 'Initialization Failed',
        description: 'Failed to initialize super admin access. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const handleKeySubmit = () => {
    // Simple verification - in production, you'd want proper authentication
    if (adminKey === '$fJtCk?9gtJNQ3qV&jZu#?$qKfRZ_M4fr3@gHxd#NSBt?NUS*5iHZPHXSARG%Seah22Srh$vc%q75t9M#*Dsek_Xf?pRSpwp!u%X') {
      initializeSuperAdmin();
    } else {
      toast({
        title: 'Invalid Key',
        description: 'The admin key is incorrect.',
        variant: 'destructive',
      });
    }
    setAdminKey('');
  };

  if (isSuperAdmin) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-green-600">Super Admin Active</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-gray-600">
            Welcome back, Milan! You have full super admin access to InTouch.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-green-500" />
              <span>All Permissions Granted</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm">
              <UserCheck className="w-4 h-4 text-blue-500" />
              <span>Super Administrator</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isTargetUser) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-gray-400" />
          </div>
          <CardTitle className="text-gray-600">Access Restricted</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-gray-600">
            Super admin access is restricted to Milan Krnjajić only.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
          <Crown className="w-6 h-6 text-white" />
        </div>
        <CardTitle>Initialize Super Admin</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-4">
            Hello Milan! Click below to initialize your super admin access for InTouch.
          </p>
        </div>

        {!showKeyInput ? (
          <Button 
            onClick={() => setShowKeyInput(true)}
            className="w-full"
            variant="outline"
          >
            <Key className="w-4 h-4 mr-2" />
            Initialize Super Admin Access
          </Button>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="adminKey">Admin Key</Label>
              <Input
                id="adminKey"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Enter super admin key"
                onKeyPress={(e) => e.key === 'Enter' && handleKeySubmit()}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleKeySubmit}
                disabled={!adminKey || isInitializing}
                className="flex-1"
              >
                {isInitializing ? 'Initializing...' : 'Initialize'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowKeyInput(false);
                  setAdminKey('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">
          This will grant you full administrative privileges over the InTouch platform.
        </div>
      </CardContent>
    </Card>
  );
}